import { useTranslation } from "common/locales";
import { FC, useEffect } from "react";
import { useNotification } from "./NotificationProvider";

interface RefreshProviderProps {
  children?: React.ReactNode;
}

/**
 * RefreshProvider handles app-level refresh logic.
 *
 * Note: Field tile polling is now handled by React Query's refetchInterval
 * in useField/useArableField hooks. Fields with no tiles will automatically
 * poll every 2 seconds until tiles are available.
 */
const RefreshProvider: FC<RefreshProviderProps> = ({ children }) => {
  const { notify } = useNotification();
  const { t } = useTranslation();

  // Auto-reload after 5 days of inactivity on farm/animal-group pages
  useEffect(() => {
    const reloadTimeout = 5 * 86400000; // 5 days
    const checkForReloadTimeout = 60000; // 60 sec
    let initialTime = Date.now();
    let timeout: NodeJS.Timeout;
    const reloadPage = () => {
      const locationMatch = /\/(farm|animal-group)\/[A-Za-z0-9]+$/.test(
        window.location.hash
      );
      clearTimeout(timeout);
      if (locationMatch && Date.now() - initialTime >= reloadTimeout) {
        initialTime = Date.now();
        notify(t("common.reloadPage"));
        console.log("Reloading the page in 6 sec");

        timeout = setTimeout(() => {
          window.location.reload();
        }, 6000); // reload in 6 seconds, so users can read the message
      } else {
        timeout = setTimeout(reloadPage, checkForReloadTimeout); // trying again every 60 sec
      }
    };
    timeout = setTimeout(reloadPage, checkForReloadTimeout);
    return () => {
      clearTimeout(timeout);
    };
  }, [t, notify]);

  return <>{children}</>;
};

export default RefreshProvider;
